import {
  DeviceConnectionStatus,
  DeviceConnectionStatusValues,
  DeviceNameCommands,
  DeviceNameProperties,
  DeviceStatusType,
  Feature,
  FeatureNames,
  Gateway,
  GenericNumberValueProperty,
  GenericStringValueProperty,
  SignalProperties,
} from '@vi-iot/client';
import { cloneDeep, sortBy } from 'lodash-es';
import { GatewayDeviceGroup } from '../gateway';
import { ZigbeeTopologyDeviceData } from './remote-device.model';
import { InstallationFeaturesState } from '../installation-features/installation-features.state';
import { InstallationFeaturesSelectors } from '../installation-features';

export class RemoteDeviceMappers {
  static MAIN_GATEWAY = 'gateway';
  static ZIGBEE_PREFIX = 'zigbee-';
  static REPEATER = 'repeater';

  static createGraphRelationsFromZigbeeDevices(
    zigbeeDevicesIds: string[],
    zigbeeDevices: GatewayDeviceGroup[],
    featuresBaseUri: string,
    stateInstallationId: number,
    stateGatewaySerial: Gateway,
    installationFeaturesState: InstallationFeaturesState
  ): ZigbeeTopologyDeviceData {
    const allZigbeeDevices: ZigbeeTopologyDeviceData[] = this.findZigbeeDevices(
      zigbeeDevicesIds,
      zigbeeDevices,
      featuresBaseUri,
      stateInstallationId,
      stateGatewaySerial,
      installationFeaturesState
    );
    const mainGatewayDevice: ZigbeeTopologyDeviceData = this.findMainGateway(
      zigbeeDevicesIds,
      zigbeeDevices,
      featuresBaseUri,
      stateInstallationId,
      stateGatewaySerial,
      installationFeaturesState
    );

    const allDevices: ZigbeeTopologyDeviceData[] = [...allZigbeeDevices, mainGatewayDevice];

    const finalStructure: ZigbeeTopologyDeviceData = this.createNestedStructureFromDevices(
      allDevices
    );

    return finalStructure;
  }

  private static findMainGateway(
    zigbeeDevicesIds: string[],
    zigbeeDevices: GatewayDeviceGroup[],
    featuresBaseUri: string,
    stateInstallationId: number,
    stateGatewaySerial: Gateway,
    installationFeaturesState: InstallationFeaturesState
  ): ZigbeeTopologyDeviceData {
    const mappedGateways: ZigbeeTopologyDeviceData[] = zigbeeDevicesIds
      .map((deviceId) => {
        const deviceParentFeature: Feature<
          GenericStringValueProperty,
          {}
        > = InstallationFeaturesSelectors.getInstallationDeviceFeature(
          FeatureNames.deviceZigbeeParent,
          deviceId,
          featuresBaseUri,
          stateInstallationId,
          stateGatewaySerial,
          installationFeaturesState
        );

        const parentId = deviceParentFeature?.properties?.value?.value.toLowerCase();
        const isParentNotADevice = !parentId;

        if (isParentNotADevice) {
          return;
        }

        // parent is present in ParentId feature but not as a device in DevicesState
        let parentDevice = null;

        zigbeeDevices.forEach((entry) => {
          if (!parentDevice) {
            parentDevice = entry.devices.find((singleDevice) => {
              return singleDevice.id.includes(parentId);
            });
          }
        });

        const isMainGateway = !parentDevice;

        if (isMainGateway) {
          return {
            parentId: `none`,
            id: parentId,
            displayName: parentId.startsWith('zigbee-')
              ? parentId.slice(7, parentId.length)
              : parentId,
            devices: [],
            name: RemoteDeviceMappers.MAIN_GATEWAY,
          };
        }
      })
      .filter((value) => !!value);

    return mappedGateways[0];
  }

  private static findZigbeeDevices(
    zigbeeDevicesIds: string[],
    zigbeeDevices: GatewayDeviceGroup[],
    featuresBaseUri: string,
    stateInstallationId: number,
    stateGatewaySerial: Gateway,
    installationFeaturesState: InstallationFeaturesState
  ): ZigbeeTopologyDeviceData[] {
    return zigbeeDevicesIds
      .map((deviceId) => {
        const deviceParentFeature: Feature<
          GenericStringValueProperty,
          {}
        > = InstallationFeaturesSelectors.getInstallationDeviceFeature(
          FeatureNames.deviceZigbeeParent,
          deviceId,
          featuresBaseUri,
          stateInstallationId,
          stateGatewaySerial,
          installationFeaturesState
        );

        const parentId = deviceParentFeature?.properties?.value?.value.toLowerCase();
        const isParentNotADevice = !parentId;

        if (isParentNotADevice) {
          return;
        }

        const lqiFeature: Feature<
          SignalProperties,
          {}
        > = InstallationFeaturesSelectors.getInstallationDeviceFeature(
          FeatureNames.deviceZigbeeLqi,
          deviceId,
          featuresBaseUri,
          stateInstallationId,
          stateGatewaySerial,
          installationFeaturesState
        );
        const deviceChildRxFeature: Feature<
          GenericNumberValueProperty,
          {}
        > = InstallationFeaturesSelectors.getInstallationDeviceFeature(
          FeatureNames.deviceZigbeeParentRx,
          deviceId,
          featuresBaseUri,
          stateInstallationId,
          stateGatewaySerial,
          installationFeaturesState
        );
        const deviceChildTxFeature: Feature<
          GenericNumberValueProperty,
          {}
        > = InstallationFeaturesSelectors.getInstallationDeviceFeature(
          FeatureNames.deviceZigbeeParentTx,
          deviceId,
          featuresBaseUri,
          stateInstallationId,
          stateGatewaySerial,
          installationFeaturesState
        );
        const deviceName: Feature<
          DeviceNameProperties,
          DeviceNameCommands
        > = InstallationFeaturesSelectors.getInstallationDeviceFeature(
          FeatureNames.deviceName,
          deviceId,
          featuresBaseUri,
          stateInstallationId,
          stateGatewaySerial,
          installationFeaturesState
        );

        const deviceConnection: Feature<
          DeviceConnectionStatus,
          {}
        > = InstallationFeaturesSelectors.getInstallationDeviceFeature(
          FeatureNames.deviceConnection,
          deviceId,
          featuresBaseUri,
          stateInstallationId,
          stateGatewaySerial,
          installationFeaturesState
        );

        const rx = deviceChildRxFeature?.properties?.value?.value / 255;
        const tx = deviceChildTxFeature?.properties?.value?.value / 255;
        const lqi = lqiFeature?.properties?.strength?.value;
        const desc = deviceName?.properties?.name?.value;
        const deviceConnectionStatus = deviceConnection?.properties?.status?.value;

        const deviceType = zigbeeDevices.find((entry) => {
          return entry.devices.find((singleDevice) => {
            return singleDevice.id.includes(deviceId);
          });
        })?.type;

        return {
          parentId: `zigbee-${parentId}`,
          displayName: deviceId.startsWith('zigbee-')
            ? deviceId.slice(7, deviceId.length)
            : deviceId,
          id: deviceId,
          rx,
          tx,
          lqiTotal: lqi,
          devices: [],
          name: deviceType,
          deviceDescription: desc,
          status:
            deviceConnectionStatus === DeviceConnectionStatusValues.connected
              ? DeviceStatusType.Online
              : DeviceStatusType.Offline,
        };
      })
      .filter((value) => !!value);
  }

  private static createNestedStructureFromDevices(
    allDevices: ZigbeeTopologyDeviceData[]
  ): ZigbeeTopologyDeviceData {
    const deviceStructure = cloneDeep(allDevices);

    deviceStructure.forEach((deviceToMove) => {
      if (deviceToMove.name === 'gateway') {
        return;
      }

      const parentIdWithoutPrefix = deviceToMove.parentId.startsWith('zigbee-')
        ? deviceToMove.parentId.slice(7, deviceToMove.parentId.length)
        : deviceToMove.parentId;

      let deviceToMoveParent;
      let canMoveDevice = false;

      deviceStructure.forEach((device) => {
        if (device === null) {
          return;
        }

        const idWithoutPrefix = device.id.startsWith('zigbee-')
          ? device.id.slice(7, device.id.length)
          : device.id;

        const isParent = idWithoutPrefix === parentIdWithoutPrefix;

        if (!isParent) {
          return;
        }

        const parentAlreadyHasDevice = device.devices.some((entry) => {
          return entry.id === deviceToMove.id;
        });

        canMoveDevice = !parentAlreadyHasDevice;
        deviceToMoveParent = device;
      });

      if (!!deviceToMoveParent && canMoveDevice) {
        deviceToMoveParent.devices.push(deviceToMove);
      }
    });

    deviceStructure.forEach((deviceToMove) => {
      deviceToMove.devices = sortBy(deviceToMove.devices, (entry) => {
        return entry.devices;
      });
    });

    return deviceStructure.find((entry) => entry.name === 'gateway');
  }
}
